import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { isTocaTenant } from '@/config/tenant';
import i18n from '@/i18n';

// API response types from backend
interface ApiDashboardStats {
  totalAssets: number;
  assetsWithoutEpc: number;
  criticalIncidents: number;
  otherIncidents: number;
  openInventoryProcesses: number;
  pendingApprovals: number;
  availablePallets: number;
  damagedPallets: number;
  assignedPallets: number;
  recoveredPallets: number;
  pendingRequisitions: number;
  pendingDeliveries: number;
  deliveredPallets: number;
  /** Pallets written off as lost (confirmed PalletIncident, Reason = Loss). */
  lostPallets: number;
  /** Bajas because the pallet itself was destroyed. Lifetime. */
  destroyedPallets?: number;
  destroyedPalletsRecent?: number;
  /** Bajas because the tag died on a pallet that was fine. Lifetime. */
  damagedEpcs?: number;
  damagedEpcsRecent?: number;
  /** The window behind the two `*Recent` figures. */
  retiredRecentDays?: number;
  /** Delivered and not delivered again within `staleDeliveredDays`. */
  staleDeliveredPallets?: number;
  staleDeliveredDays?: number;
}

interface ApiRecentIncident {
  id: number;
  assetId: string | null;
  assetName: string;
  type: string;
  isCritical: boolean;
  createdDate: string;
  locationName: string | null;
}

interface ApiInventoryStats {
  sameDay: number;
  oneToThreeDays: number;
  fourToSevenDays: number;
  moreThanSevenDays: number;
  totalClosed: number;
}

interface ApiTrendData {
  month: string;
  deliveries: number;
  recoveries: number;
}

interface ApiRecentRequisition {
  id: number;
  folio: string;
  clientName: string;
  requestedQuantity: number;
  suppliedQuantity: number;
  status: string;
  requestedDate: string;
}

export interface DashboardStats {
  // Core stats (both tenants)
  totalAssets: number;
  criticalIncidents: number;
  otherIncidents: number;
  assetsWithoutEpc: number;
  openInventories: number;

  // Toca-specific stats
  availablePallets?: number;
  damagedPallets?: number;
  assignedPallets?: number;
  recoveredPallets?: number;
  pendingDeliveries?: number;
  pendingRequisitions?: number;
  deliveredPallets?: number;
  lostPallets?: number;

  // Bajas: counted from the baja folios, never from asset status. A retired
  // pallet is inactive, so it already left every count above.
  destroyedPallets?: number;
  destroyedPalletsRecent?: number;
  damagedEpcs?: number;
  damagedEpcsRecent?: number;
  retiredRecentDays?: number;
  staleDeliveredPallets?: number;
  staleDeliveredDays?: number;
}

export interface RecentIncident {
  id: number;
  type: string;
  assetCode: string;
  priority: string;
  createdAt: string;
  hasImages: boolean;
}

export interface InventoryStats {
  range: string;
  count: number;
}

export interface TrendData {
  month: string;
  deliveries: number;
  recoveries: number;
}

export interface AssetByStatus {
  status: number;
  statusLabel: string;
  count: number;
  color: string;
}

export interface AssetByClass {
  className: string;
  classCode: string;
  count: number;
}

export interface AssetTimelineItem {
  month: string;
  count: number;
}

export interface IncidentTrend {
  month: string;
  created: number;
  resolved: number;
}

export interface RecentRequisition {
  id: number;
  folio: string;
  clientName: string;
  requestedQuantity: number;
  suppliedQuantity: number;
  status: 'Requested' | 'Approved' | 'InProgress' | 'Completed' | 'Rejected';
  requestedDate: string;
}

// Query keys
export const dashboardKeys = {
  all: ['dashboard'] as const,
  stats: () => [...dashboardKeys.all, 'stats'] as const,
  recentIncidents: () => [...dashboardKeys.all, 'recent-incidents'] as const,
  inventoryStats: () => [...dashboardKeys.all, 'inventory-stats'] as const,
  trends: () => [...dashboardKeys.all, 'trends'] as const,
  assetsByStatus: () => [...dashboardKeys.all, 'assets-by-status'] as const,
  assetsByClass: () => [...dashboardKeys.all, 'assets-by-class'] as const,
  assetsTimeline: () => [...dashboardKeys.all, 'assets-timeline'] as const,
  incidentTrends: () => [...dashboardKeys.all, 'incident-trends'] as const,
};

// Transform API stats to frontend format
function transformStats(apiStats: ApiDashboardStats): DashboardStats {
  return {
    totalAssets: apiStats.totalAssets,
    criticalIncidents: apiStats.criticalIncidents,
    otherIncidents: apiStats.otherIncidents,
    assetsWithoutEpc: apiStats.assetsWithoutEpc,
    openInventories: apiStats.openInventoryProcesses,
    availablePallets: apiStats.availablePallets,
    damagedPallets: apiStats.damagedPallets,
    assignedPallets: apiStats.assignedPallets,
    recoveredPallets: apiStats.recoveredPallets,
    pendingDeliveries: apiStats.pendingDeliveries,
    pendingRequisitions: apiStats.pendingRequisitions,
    deliveredPallets: apiStats.deliveredPallets,
    lostPallets: apiStats.lostPallets ?? 0,
    destroyedPallets: apiStats.destroyedPallets ?? 0,
    destroyedPalletsRecent: apiStats.destroyedPalletsRecent ?? 0,
    damagedEpcs: apiStats.damagedEpcs ?? 0,
    damagedEpcsRecent: apiStats.damagedEpcsRecent ?? 0,
    retiredRecentDays: apiStats.retiredRecentDays ?? 30,
    staleDeliveredPallets: apiStats.staleDeliveredPallets ?? 0,
    staleDeliveredDays: apiStats.staleDeliveredDays ?? 90,
  };
}

// Transform API recent incident to frontend format
function transformRecentIncident(apiIncident: ApiRecentIncident): RecentIncident {
  return {
    id: Number(apiIncident.id),
    type: apiIncident.type,
    assetCode: apiIncident.assetId || apiIncident.assetName,
    priority: apiIncident.isCritical ? 'Critical' : 'Normal',
    createdAt: apiIncident.createdDate,
    hasImages: false,
  };
}

// Transform API inventory stats to frontend format
function transformInventoryStats(apiStats: ApiInventoryStats): InventoryStats[] {
  return [
    { range: 'Same Day', count: apiStats.sameDay },
    { range: '1-3 Days', count: apiStats.oneToThreeDays },
    { range: '4-7 Days', count: apiStats.fourToSevenDays },
    { range: '7+ Days', count: apiStats.moreThanSevenDays },
  ];
}

// Get dashboard stats
export function useDashboardStats() {
  return useQuery({
    queryKey: dashboardKeys.stats(),
    queryFn: async () => {
      const response = await api.get<ApiDashboardStats>('/api/dashboard/stats');
      return transformStats(response.data);
    },
    refetchInterval: 1000 * 60 * 5, // Refetch every 5 minutes
  });
}

// Get recent incidents
export function useRecentIncidents(limit = 5) {
  return useQuery({
    queryKey: dashboardKeys.recentIncidents(),
    queryFn: async () => {
      const response = await api.get<ApiRecentIncident[]>(`/api/dashboard/recent-incidents?limit=${limit}`);
      return response.data.map(transformRecentIncident);
    },
    refetchInterval: 1000 * 60 * 5,
  });
}

// Get inventory completion stats
export function useInventoryStats(enabled = true) {
  return useQuery({
    queryKey: dashboardKeys.inventoryStats(),
    enabled,
    queryFn: async () => {
      const response = await api.get<ApiInventoryStats>('/api/dashboard/inventory-stats');
      return transformInventoryStats(response.data);
    },
    refetchInterval: 1000 * 60 * 5,
  });
}

// Get delivery/recovery trends (Toca only)
export function useTrends(months = 6) {
  return useQuery({
    queryKey: dashboardKeys.trends(),
    queryFn: async () => {
      const response = await api.get<ApiTrendData[]>(`/api/dashboard/trends?months=${months}`);
      return response.data as TrendData[];
    },
    refetchInterval: 1000 * 60 * 60, // Refetch every hour
  });
}

// Status label/color mapping. Tenant-aware:
//   - SGS (AssetStatus): Ok=1, Location=2, Incident=3, Pending=4 (+ legacy 0=OK)
//   - Toca (AssetStatusColumn): Registered=1, Delivered=2, Recovered=3, InTransit=4, Damaged=5, Available=6
const SGS_STATUS_META: Record<number, { label: string; color: string }> = {
  0: { label: 'OK', color: 'hsl(142, 71%, 45%)' },
  1: { label: 'Pending', color: 'hsl(45, 93%, 47%)' },
  2: { label: 'Maintenance', color: 'hsl(217, 91%, 60%)' },
  3: { label: 'Decommissioned', color: 'hsl(0, 84%, 60%)' },
};

const TOCA_STATUS_META: Record<number, { label: string; color: string }> = {
  1: { label: 'Registered', color: 'hsl(217, 91%, 60%)' },  // blue
  2: { label: 'Delivered', color: 'hsl(270, 71%, 55%)' },   // purple
  3: { label: 'Recovered', color: 'hsl(142, 71%, 45%)' },   // green
  4: { label: 'In Transit', color: 'hsl(45, 93%, 47%)' },   // amber
  5: { label: 'Damaged', color: 'hsl(0, 84%, 60%)' },       // red
  6: { label: 'Available', color: 'hsl(160, 84%, 39%)' },   // emerald
};

const STATUS_META: Record<number, { label: string; color: string }> = isTocaTenant()
  ? TOCA_STATUS_META
  : SGS_STATUS_META;

// Get assets grouped by status
export function useAssetsByStatus(enabled = true) {
  return useQuery({
    queryKey: dashboardKeys.assetsByStatus(),
    enabled,
    queryFn: async () => {
      const response = await api.get<{ status: number; count: number }[]>('/api/dashboard/assets-by-status');
      return response.data.map((item): AssetByStatus => ({
        status: item.status,
        // Localize via the per-tenant common.statuses.asset.<code> catalog,
        // falling back to the hardcoded English meta for any unmapped code.
        statusLabel: i18n.t(`common:statuses.asset.${item.status}`, {
          defaultValue: STATUS_META[item.status]?.label ?? `Status ${item.status}`,
        }),
        count: item.count,
        color: STATUS_META[item.status]?.color ?? 'hsl(0, 0%, 60%)',
      }));
    },
    refetchInterval: 1000 * 60 * 5,
  });
}

// Get assets grouped by class (parent)
export function useAssetsByClass(enabled = true) {
  return useQuery({
    queryKey: dashboardKeys.assetsByClass(),
    enabled,
    queryFn: async () => {
      const response = await api.get<AssetByClass[]>('/api/dashboard/assets-by-class');
      return response.data;
    },
    refetchInterval: 1000 * 60 * 5,
  });
}

// Get asset registration timeline (assets added per month)
export function useAssetsTimeline(months = 6) {
  return useQuery({
    queryKey: dashboardKeys.assetsTimeline(),
    queryFn: async () => {
      const response = await api.get<AssetTimelineItem[]>(`/api/dashboard/assets-timeline?months=${months}`);
      return response.data;
    },
    refetchInterval: 1000 * 60 * 5,
  });
}

// Get incident trends (created vs resolved per month)
export function useIncidentTrends(months = 6, enabled = true) {
  return useQuery({
    queryKey: dashboardKeys.incidentTrends(),
    enabled,
    queryFn: async () => {
      const response = await api.get<IncidentTrend[]>(`/api/dashboard/incident-trends?months=${months}`);
      return response.data;
    },
    refetchInterval: 1000 * 60 * 5,
  });
}

// --- Drill-down types and hook ---

export type DrillDownKpi =
  | 'assetsWithoutEpc'
  | 'availablePallets'
  | 'damagedPallets'
  | 'assignedPallets'
  | 'recoveredPallets'
  | 'staleDelivered';

export interface DrillDownAssetRecord {
  assetId: string;
  name: string | null;
  epc: string | null;
  locationName: string | null;
  assetStatus: number;
  statusLabel: string;
  createdDate: string;
}

export interface DrillDownRecoveryRecord {
  recoverPalletId: number;
  epc: string;
  clientName: string | null;
  locationName: string | null;
  scanDate: string;
  completeDate: string | null;
}

export interface DrillDownPage {
  type: 'asset' | 'recovery';
  data: DrillDownAssetRecord[] | DrillDownRecoveryRecord[];
  totalCount: number;
  page: number;
  pageSize: number;
}

const DRILLDOWN_PAGE_SIZE = 100;

export function useDrillDown(kpi: DrillDownKpi | null, search?: string) {
  return useInfiniteQuery({
    queryKey: [...dashboardKeys.all, 'drilldown', kpi, search ?? ''],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await api.get<DrillDownPage>('/api/dashboard/drilldown', {
        params: { kpi, search: search || undefined, page: pageParam, pageSize: DRILLDOWN_PAGE_SIZE },
      });
      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const fetched = lastPage.page * lastPage.pageSize;
      return fetched < lastPage.totalCount ? lastPage.page + 1 : undefined;
    },
    enabled: kpi !== null,
  });
}

// Get recent requisitions (Toca only)
export function useRecentRequisitions(limit = 5) {
  return useQuery({
    queryKey: [...dashboardKeys.all, 'recent-requisitions'],
    queryFn: async () => {
      const response = await api.get<ApiRecentRequisition[]>(`/api/dashboard/recent-requisitions?limit=${limit}`);
      return response.data.map(r => ({
        id: Number(r.id),
        folio: r.folio,
        clientName: r.clientName,
        requestedQuantity: r.requestedQuantity,
        suppliedQuantity: r.suppliedQuantity,
        status: r.status as RecentRequisition['status'],
        requestedDate: r.requestedDate,
      })) as RecentRequisition[];
    },
    refetchInterval: 1000 * 60 * 5, // Refetch every 5 minutes
  });
}

// --- Monthly operations (Pallet tenants) ---
// Shape of MonthlyOperationsQuery.Result. Months are Mexico City months.

/** Every pallet movement of one calendar month, as counted by the API. */
export interface MonthlyOperationsSummary {
  /** Pallets supplied on completed requisitions, dated by when they were supplied. */
  delivered: number;
  /** Quantity of recovery folios completed in the month. */
  recovered: number;
  /** ...of which clients' own folios ("devueltas") */
  recoveredFromClients: number;
  /** ...and folios recovered at a CEDIS. */
  recoveredAtCedis: number;
  requisitionsCreated: number;
  requestedPallets: number;
  requisitionsCompleted: number;
  /** Requested vs supplied on the requisitions completed in the month. */
  completedRequested: number;
  completedSupplied: number;
  /** % of requested pallets supplied (over-delivery capped per requisition). */
  fillRate: number | null;
  /** % of completed requisitions supplied by the date the client asked for. */
  onTimeRate: number | null;
  /** Mean days from a requisition being raised to it being supplied. */
  avgFulfillmentDays: number | null;
  damaged: number;
  repaired: number;
  palletsDestroyed: number;
  epcsDamaged: number;
  lost: number;
  qualityReturns: number;
  /** Pallets clients reported transferring onward. */
  transferred: number;
  newPallets: number;
  /** Pallets received from manufacture providers. */
  manufactured: number;
  inventoriesClosed: number;
}

export interface MonthlyOperationsDay {
  /** Day of month, 1-based. */
  day: number;
  delivered: number;
  recovered: number;
}

export interface MonthlyOperationsClient {
  clientId: number;
  clientName: string;
  delivered: number;
  /** The client's own recovery folios, as the saldo counts them. */
  returned: number;
  /** Pallets recovered at a CEDIS that were last delivered to this client. */
  recoveredAtCedis: number;
  transferred: number;
  lost: number;
  damaged: number;
  /** The month's change to the saldo, by the tenant's balance formula. */
  net: number;
  /** The saldo today. */
  balance: number;
}

export interface MonthlyRequisitionStatus {
  /** RequisitionStatus: 1 Requested, 2 Approved, 3 Rejected, 4 InProgress, 5 Completed. */
  status: number;
  count: number;
  requested: number;
  supplied: number;
}

export interface MonthlyOperations {
  year: number;
  /** 1-12. */
  month: number;
  current: MonthlyOperationsSummary;
  previous: MonthlyOperationsSummary;
  daily: MonthlyOperationsDay[];
  byClient: MonthlyOperationsClient[];
  /** Requisitions raised in the month, by their status today. */
  requisitionsByStatus: MonthlyRequisitionStatus[];
}

/**
 * One calendar month of pallet operations. Pallet tenants only; the API answers
 * 404 elsewhere. Past months never change, so they are cached for an hour; the
 * current month refreshes with the rest of the dashboard.
 */
export function useMonthlyOperations(year: number, month: number, enabled = true) {
  const now = new Date();
  const isCurrentMonth = now.getFullYear() === year && now.getMonth() + 1 === month;
  return useQuery({
    queryKey: [...dashboardKeys.all, 'monthly-operations', year, month],
    queryFn: async () => {
      const response = await api.get<MonthlyOperations>('/api/dashboard/monthly-operations', {
        params: { year, month },
      });
      return response.data;
    },
    enabled,
    placeholderData: (previous) => previous,
    staleTime: isCurrentMonth ? 0 : 1000 * 60 * 60,
    refetchInterval: isCurrentMonth ? 1000 * 60 * 5 : false,
  });
}
